import React from "react";
import { useLocation } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import { ownerApi } from "../../../../api/owner";
import { formatApiError } from "../../../../api/client";
import { dateInputValue } from "../center/ownerCenterUi";

const ITEM_TYPES = [
  { value: "action", label: "Tarea / pendiente" },
  { value: "fix", label: "Fix" },
  { value: "idea", label: "Idea" },
  { value: "meeting", label: "Reunión" },
  { value: "note", label: "Nota" },
];

const STATE_OPTIONS = [
  { value: "ready", label: "Lista" },
  { value: "in_progress", label: "Haciendo" },
  { value: "waiting", label: "Esperando" },
  { value: "blocked", label: "Trabada" },
];

const ESTIMATES = [15, 30, 45, 60, 90, 120, 180, 240, 360, 480];

function idOf(value) {
  return String(value?.id || value?._id || value || "");
}


function dependencyWouldCycle(candidate, currentTask, rows = []) {
  const currentId = idOf(currentTask);
  if (!currentId) return false;
  const byId = new Map(rows.map((row) => [idOf(row), row]).filter(([id]) => Boolean(id)));
  const visited = new Set();
  let cursor = idOf(candidate);
  while (cursor) {
    if (cursor === currentId) return true;
    if (visited.has(cursor)) return true;
    visited.add(cursor);
    cursor = idOf(byId.get(cursor)?.parentCommitmentId);
  }
  return false;
}

function emptyForm(initiative) {
  return {
    title: "",
    projectItemType: "action",
    state: "ready",
    projectRole: "",
    parentCommitmentId: "",
    definitionOfDone: "",
    waitingFor: "",
    clientId: idOf(initiative?.clientId),
    blockedReason: "",
    unblockAction: "",
    reviewAt: "",
    scheduledStartAt: "",
    dueAt: "",
    estimatedMinutes: "",
    priority: "normal",
    notes: "",
    initiativeId: idOf(initiative),
  };
}

function formFromTask(task, initiative) {
  if (!task) return emptyForm(initiative);
  return {
    title: task.title || "",
    projectItemType: task.projectItemType || "action",
    state: task?.work?.state || (task.projectRole === "blocker" ? "blocked" : "ready"),
    projectRole: task.projectRole || "",
    parentCommitmentId: idOf(task.parentCommitmentId),
    definitionOfDone: task?.work?.definitionOfDone || "",
    waitingFor: task?.work?.waitingFor || "",
    clientId: idOf(task?.clientId) || idOf(initiative?.clientId),
    blockedReason: task?.work?.blockedReason || task?.work?.blockedBy || "",
    unblockAction: task?.work?.unblockAction || "",
    reviewAt: dateInputValue(task?.work?.reviewAt),
    scheduledStartAt: dateInputValue(task.scheduledStartAt),
    dueAt: dateInputValue(task.dueAt),
    estimatedMinutes: task.estimatedMinutes ? String(task.estimatedMinutes) : "",
    priority: task.priority || "normal",
    notes: task.notes || "",
    initiativeId: idOf(initiative),
  };
}

export default function OwnerProjectTaskDialog({ open, onClose, onSaved, initiative, task = null, tasks = [] }) {
  const location = useLocation();
  const jarvisUiHandledRef = React.useRef("");
  const [form, setForm] = React.useState(() => emptyForm(initiative));
  const [moreOpen, setMoreOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [people, setPeople] = React.useState([]);
  const [peopleLoading, setPeopleLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const editing = Boolean(task?.id || task?._id);

  React.useEffect(() => {
    if (!open) return;
    setForm(formFromTask(task, initiative));
    setMoreOpen(Boolean(task?.notes || task?.dueAt || task?.projectRole || task?.parentCommitmentId));
    setError("");
  }, [open, task, initiative]);

  React.useEffect(() => {
    if (!open) return;
    const ui = location.state?.ownerJarvisUi || null;
    if (!ui || ui.surface !== "project360" || !["task_new", "task_edit"].includes(ui.form)) return;
    if (String(ui.initiativeId || "") !== idOf(initiative)) return;
    if (ui.form === "task_edit" && ui.taskId && String(ui.taskId) !== idOf(task)) return;
    const token = String(ui.token || `${ui.form}:${ui.taskId || ""}:${ui.focusField || ""}:${JSON.stringify(ui.preset || {})}`);
    if (jarvisUiHandledRef.current === token) return;
    jarvisUiHandledRef.current = token;

    if (ui.preset && typeof ui.preset === "object") {
      setForm((current) => ({ ...current, ...ui.preset }));
    }
    if (ui.focusField) {
      window.setTimeout(() => {
        document.getElementById(`owner-project-task-${ui.focusField}`)?.focus();
      }, 100);
    }
  }, [initiative, location.state, open, task]);

  React.useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setPeopleLoading(true);
    ownerApi.listClients({ limit: 250 })
      .then((response) => { if (active) setPeople(response?.clients || []); })
      .catch(() => { if (active) setPeople([]); })
      .finally(() => { if (active) setPeopleLoading(false); });
    return () => { active = false; };
  }, [open]);

  const possibleParents = tasks.filter((row) => {
    const id = idOf(row);
    if (!id || id === idOf(task) || row.status === "cancelled") return false;
    return !dependencyWouldCycle(row, task, tasks);
  });
  const currentDependency = task?.dependency && idOf(task.dependency) ? task.dependency : null;
  const parentOptions = currentDependency && !possibleParents.some((row) => idOf(row) === idOf(currentDependency))
    ? [currentDependency, ...possibleParents]
    : possibleParents;

  const set = (name, value) => setForm((current) => ({ ...current, [name]: value }));
  const selectState = (state) => setForm((current) => ({ ...current, state, projectRole: state === "blocked" ? "blocker" : current.projectRole === "blocker" ? "" : current.projectRole }));
  const selectType = (projectItemType) => setForm((current) => ({
    ...current,
    projectItemType,
    state: ["objective", "idea", "note"].includes(projectItemType) ? "ready" : current.state,
    projectRole: ["objective", "idea", "note"].includes(projectItemType) ? "" : current.projectRole,
    estimatedMinutes: ["objective", "idea", "note"].includes(projectItemType) ? "" : current.estimatedMinutes,
    scheduledStartAt: ["objective", "idea", "note"].includes(projectItemType) ? "" : current.scheduledStartAt,
  }));
  const actionableType = ["action", "fix", "meeting"].includes(form.projectItemType);

  const submit = async () => {
    if (!form.title.trim()) { setError("Decime qué hay que hacer."); return; }
    if (form.state === "waiting" && !form.waitingFor.trim()) { setError("Decime qué estás esperando."); return; }
    if (form.state === "waiting" && !form.reviewAt) { setError("Definí cuándo querés volver a revisar esta espera."); return; }
    if (form.state === "blocked" && !form.blockedReason.trim()) { setError("Decime qué está trabando esta tarea."); return; }
    setSaving(true); setError("");
    try {
      const payload = {
        title: form.title.trim(),
        kind: "project_next_step",
        priority: form.priority,
        projectItemType: form.projectItemType,
        projectRole: form.projectRole,
        parentCommitmentId: form.parentCommitmentId || null,
        initiativeId: idOf(initiative),
        clientId: form.clientId || initiative?.clientId || null,
        estimatedMinutes: form.estimatedMinutes ? Number(form.estimatedMinutes) : null,
        scheduledStartAt: form.scheduledStartAt || null,
        dueAt: form.dueAt || null,
        notes: form.notes,
        work: {
          state: form.state,
          definitionOfDone: form.definitionOfDone,
          waitingFor: form.state === "waiting" ? form.waitingFor : "",
          ...(form.state === "blocked" ? { blockedReason: form.blockedReason, unblockAction: form.unblockAction } : {}),
          reviewAt: ["waiting", "blocked"].includes(form.state) ? (form.reviewAt || null) : null,
        },
        source: { kind: "manual" },
      };
      const response = editing
        ? await ownerApi.updateCommitment(idOf(task), payload)
        : await ownerApi.createCommitment(payload);
      await onSaved?.(response?.commitment || null);
      onClose?.();
    } catch (err) {
      setError(formatApiError(err, editing ? "No pude actualizar la tarea." : "No pude crear la tarea."));
    } finally {
      setSaving(false);
    }
  };

  return <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
    <DialogTitle sx={{ pb: .8 }}>
      <Typography sx={{ fontWeight: 1000, fontSize: { xs: "1.1rem", sm: "1.3rem" } }}>{editing ? "Editar elemento" : "Anotar en el proyecto"}</Typography>
      <Typography sx={{ mt: .2, color: "text.secondary", fontSize: ".78rem" }}>{initiative?.name || "Proyecto"} · con el título alcanza. Fecha, duración y estado son opcionales.</Typography>
    </DialogTitle>
    <DialogContent>
      <Stack spacing={1.35} sx={{ pt: .7 }}>
        <TextField id="owner-project-task-title" autoFocus fullWidth label="Pendiente" placeholder="Ej.: arreglar los fixes que pasó Agustín" value={form.title} onChange={(e) => set("title", e.target.value)} />

        <Button onClick={() => setMoreOpen((value) => !value)} endIcon={moreOpen ? <KeyboardArrowUpRoundedIcon /> : <KeyboardArrowDownRoundedIcon />} sx={{ alignSelf: "flex-start", px: .2, textTransform: "none", fontWeight: 900 }}>Más detalle</Button>

        {moreOpen ? <>
        <Box id="owner-project-task-projectItemType" tabIndex={-1}>
          <Typography sx={{ mb: .65, fontWeight: 900, fontSize: ".75rem", color: "text.secondary" }}>¿QUÉ ES?</Typography>
          <Stack direction="row" spacing={.55} useFlexGap flexWrap="wrap">
            {ITEM_TYPES.map((option) => <Chip key={option.value} clickable onClick={() => selectType(option.value)} label={option.label} color={form.projectItemType === option.value ? "primary" : "default"} variant={form.projectItemType === option.value ? "filled" : "outlined"} sx={{ fontWeight: 900, minHeight: 32, px: .25 }} />)}
          </Stack>
          <Typography sx={{ mt: .45, color: "text.secondary", fontSize: ".68rem" }}>Ideas y notas ordenan el proyecto pero no se convierten en trabajo de Today.</Typography>
        </Box>

        {actionableType ? <Box id="owner-project-task-state" tabIndex={-1}>
          <Typography sx={{ mb: .65, fontWeight: 900, fontSize: ".75rem", color: "text.secondary" }}>¿CÓMO ESTÁ?</Typography>
          <Stack direction="row" spacing={.55} useFlexGap flexWrap="wrap">
            {STATE_OPTIONS.map((option) => <Chip key={option.value} clickable onClick={() => selectState(option.value)} label={option.label} color={form.state === option.value ? (option.value === "blocked" ? "error" : option.value === "waiting" ? "warning" : "primary") : "default"} variant={form.state === option.value ? "filled" : "outlined"} sx={{ fontWeight: 900, minHeight: 34, px: .4 }} />)}
          </Stack>
        </Box> : null}

        {actionableType && form.state === "waiting" ? <Stack spacing={1.05}>
          <TextField id="owner-project-task-waitingFor" fullWidth label="Qué estoy esperando" placeholder="Ej.: las credenciales de acceso" value={form.waitingFor} onChange={(e) => set("waitingFor", e.target.value)} />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.1}>
            <FormControl fullWidth disabled={peopleLoading}>
              <InputLabel>De quién (opcional)</InputLabel>
              <Select label="De quién (opcional)" value={form.clientId} onChange={(e) => set("clientId", e.target.value)}>
                <MenuItem value="">Elegir persona</MenuItem>
                {people.map((person) => <MenuItem key={idOf(person)} value={idOf(person)}>{person.name}{person.company ? ` · ${person.company}` : ""}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField id="owner-project-task-reviewAt" fullWidth required type="datetime-local" label="Volver a revisar" helperText="Si para entonces no llegó, Owner lo vuelve a poner en atención." InputLabelProps={{ shrink: true }} value={form.reviewAt} onChange={(e) => set("reviewAt", e.target.value)} />
          </Stack>
        </Stack> : null}

        {actionableType && form.state === "blocked" ? <Stack spacing={1.05}>
          <TextField id="owner-project-task-blockedReason" fullWidth required label="Qué la bloquea" placeholder="Ej.: No tenemos acceso al dominio" value={form.blockedReason} onChange={(e) => set("blockedReason", e.target.value)} helperText="Describí el impedimento real. No es lo mismo que estar esperando a una persona." />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.1}>
            <TextField id="owner-project-task-unblockAction" fullWidth label="Qué la desbloquearía (opcional)" placeholder="Ej.: Recuperar acceso desde el registrador" value={form.unblockAction} onChange={(e) => set("unblockAction", e.target.value)} helperText="Sólo cargalo si ya sabés la acción. Owner/Jarvis no la inventan." />
            <TextField id="owner-project-task-reviewAt" fullWidth type="datetime-local" label="Volver a revisar" InputLabelProps={{ shrink: true }} value={form.reviewAt} onChange={(e) => set("reviewAt", e.target.value)} />
          </Stack>
        </Stack> : null}

        <TextField id="owner-project-task-definitionOfDone" fullWidth label={form.projectItemType === "objective" ? "Se cumple cuando…" : "Está terminado cuando…"} placeholder={form.projectItemType === "objective" ? "Ej.: Fase 3 entregada y lista para cobrar" : "Ej.: checkout publicado y compra de prueba aprobada"} value={form.definitionOfDone} onChange={(e) => set("definitionOfDone", e.target.value)} />

        {actionableType && !["waiting", "blocked"].includes(form.state) ? <Stack direction={{ xs: "column", sm: "row" }} spacing={1.1}>
          <FormControl fullWidth><InputLabel>Duración aproximada</InputLabel><Select id="owner-project-task-estimatedMinutes" label="Duración aproximada" value={form.estimatedMinutes} onChange={(e) => set("estimatedMinutes", e.target.value)}><MenuItem value="">Después</MenuItem>{ESTIMATES.map((minutes) => <MenuItem key={minutes} value={String(minutes)}>{minutes < 60 ? `${minutes} min` : minutes % 60 === 0 ? `${minutes / 60} h` : `${Math.floor(minutes / 60)} h ${minutes % 60} min`}</MenuItem>)}</Select></FormControl>
          <TextField id="owner-project-task-scheduledStartAt" fullWidth type="datetime-local" label={form.projectItemType === "meeting" ? "Horario de la reunión" : "Agendar (opcional)"} helperText={form.projectItemType === "meeting" ? "Horario real del encuentro." : "No hace falta decidirlo ahora; Today puede hacerlo después."} InputLabelProps={{ shrink: true }} value={form.scheduledStartAt} onChange={(e) => set("scheduledStartAt", e.target.value)} />
        </Stack> : null}

        </> : null}
        <Collapse in={moreOpen} unmountOnExit>
          <Stack spacing={1.15}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.1}>
              <FormControl fullWidth><InputLabel>Nivel</InputLabel><Select label="Nivel" value={form.projectRole} onChange={(e) => set("projectRole", e.target.value)}><MenuItem value="">Normal</MenuItem><MenuItem value="blocker">Bloqueante</MenuItem><MenuItem value="microtask">Micro</MenuItem></Select></FormControl>
              <FormControl fullWidth>
                <InputLabel>Depende de</InputLabel>
                <Select label="Depende de" value={form.parentCommitmentId} onChange={(e) => set("parentCommitmentId", e.target.value)}>
                  <MenuItem value="">No depende de otra acción</MenuItem>
                  {parentOptions.map((row) => <MenuItem key={idOf(row)} value={idOf(row)}>{row.title}{row.status === "done" ? " · hecha" : row?.executability?.executable === false ? " · todavía no disponible" : ""}</MenuItem>)}
                </Select>
                <Typography sx={{ mt: .35, color: "text.secondary", fontSize: ".68rem" }}>La acción elegida tiene que quedar hecha antes. Puede depender a su vez de otro paso; Owner evita ciclos.</Typography>
              </FormControl>
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.1}>
              <TextField fullWidth type="datetime-local" label="Fecha límite" InputLabelProps={{ shrink: true }} value={form.dueAt} onChange={(e) => set("dueAt", e.target.value)} />
              <FormControl fullWidth><InputLabel>Prioridad</InputLabel><Select label="Prioridad" value={form.priority} onChange={(e) => set("priority", e.target.value)}><MenuItem value="low">Baja</MenuItem><MenuItem value="normal">Normal</MenuItem><MenuItem value="high">Alta</MenuItem></Select></FormControl>
            </Stack>
            <TextField fullWidth multiline minRows={2} label="Contexto / notas" value={form.notes} onChange={(e) => set("notes", e.target.value)} />
          </Stack>
        </Collapse>

        {error ? <Alert severity="error">{error}</Alert> : null}
      </Stack>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2.2 }}>
      <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none", fontWeight: 850 }}>Cancelar</Button>
      <Button variant="contained" onClick={submit} disabled={saving} sx={{ textTransform: "none", fontWeight: 950 }}>{saving ? "Guardando…" : editing ? "Guardar cambios" : "Guardar"}</Button>
    </DialogActions>
  </Dialog>;
}
